import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import { ChartData, ChartOptions } from 'chart.js';
import { AvailableIcons, Icon, Tooltip } from 'src/components-dummy';
import { kpiService } from 'src/services/src/service/analytics/kpi.service';
import { RevenueByDeviceDataItem } from 'src/services/src/service/types/analytics';
import { TimeRangeEnum } from '../FilterSection/TimeRangeSelector/types';
import PieChart from '../PieChart/PieChart';
import { formatNumberShort } from '../../utils/utils';

interface DeviceRevenueDistributionProps {
  timeRange: TimeRangeEnum;
  product: string;
  shopId: number;
}

const DEVICE_LABELS: Record<string, string> = {
  mobile: 'Mobile',
  desktop: 'Desktop',
  tablet: 'Tablet',
  unknown: 'Unknown',
};

const DEVICE_COLORS: Record<string, string> = {
  mobile: '#005CCC',
  desktop: '#4CD7D0',
  tablet: '#FFD54F',
  unknown: '#90A4AE',
};

export const DeviceRevenueDistribution = ({
  timeRange,
  product,
  shopId,
}: DeviceRevenueDistributionProps): JSX.Element => {
  const [values, setValues] = useState<RevenueByDeviceDataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;
    setLoading(true);
    setError(null);

    const fetchValues = async () => {
      try {
        const remappedProduct = product === 'All Syte Products' ? 'all' : product;
        const response = await kpiService.getRevenueByDevice({
          shopId,
          timeRange,
          product: remappedProduct,
        });
        if (isActive) {
          setValues(response.data || []);
          setLoading(false);
        }
      } catch (err: any) {
        if (isActive) {
          setError(err?.message || 'Error fetching revenue by device');
          setLoading(false);
        }
      }
    };

    fetchValues();

    return () => {
      isActive = false;
    };
  }, [shopId, timeRange, product]);

  const chartData = useMemo<ChartData<'doughnut'> | null>(() => {
    if (!values.length) return null;

    return {
      labels: values.map(item => DEVICE_LABELS[item.device] || item.device),
      datasets: [
        {
          data: values.map(item => item.revenue),
          backgroundColor: values.map(item => DEVICE_COLORS[item.device] || DEVICE_COLORS.unknown),
          borderWidth: 0,
          hoverOffset: 10,
        },
      ],
    };
  }, [values]);

  const pieOptions = useMemo<ChartOptions<'doughnut'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '40%',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: ctx => {
              const value = Number(ctx.raw || 0);
              const dataset = (ctx.dataset.data || []) as Array<number | string>;
              const total = dataset.reduce<number>((sum, v) => sum + Number(v), 0);
              const percent = total > 0 ? (value / total) * 100 : 0;
              return `${ctx.label}: $${formatNumberShort(value)} (${percent.toFixed(1)}%)`;
            },
          },
        },
      },
      layout: {
        padding: {
          top: 20,
          bottom: 20,
          left: 20,
          right: 20,
        },
      },
    }),
    []
  );

  return (
    <Box>
      <Box mb={2} display='flex' alignItems='center'>
        <Typography variant='h6' sx={{ fontWeight: 700, width: 'fit-content' }}>
          Revenue by Device
        </Typography>
        <Tooltip value='Syte revenue split by device for the selected time range and product. Excludes bot traffic and is not affected by the device filter.'>
          <IconButton size='small' sx={{ ml: 1, color: '#6E6E71' }}>
            <Icon name={AvailableIcons.InformationV3} />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            minHeight: 320,
            mt: 2,
          }}
        >
          <Skeleton variant='circular' width={220} height={220} />
          <Skeleton variant='text' width={180} height={24} sx={{ mt: 3 }} />
        </Box>
      ) : error ? (
        <Box color='error.main' textAlign='center' py={4}>
          <Typography color='error'>{error}</Typography>
        </Box>
      ) : chartData ? (
        <PieChart data={chartData} options={pieOptions} />
      ) : (
        <Box textAlign='center' py={6}>
          <Typography color='text.secondary'>No data available.</Typography>
        </Box>
      )}
    </Box>
  );
};
